/**
 * Maps Cash Flow Ratios table rows (API `name` keys) onto the shared
 * sector-aware benchmark system. Scoring itself lives in
 * cashFlowBenchmarkResolver.js — this file only decides how each row is treated.
 */

export const CASH_FLOW_RATIOS_PANEL_UNMAPPED = {
    kind: 'notScored',
    reason: 'Unmapped Cash Flow Ratios row; not scored until a normalized cash-flow metric is defined.',
};

/**
 * @typedef {'scored' | 'selfSufficiency' | 'notScored'} CashFlowRatiosPanelKind
 * @typedef {{ kind: 'scored', metricId: string }} ScoredRowMapping
 * @typedef {{ kind: 'selfSufficiency' }} SelfSufficiencyRowMapping
 * @typedef {{ kind: 'notScored', reason: string }} NotScoredRowMapping
 * @typedef {ScoredRowMapping | SelfSufficiencyRowMapping | NotScoredRowMapping} CashFlowRatiosPanelMapping
 */

/** @type {Record<string, CashFlowRatiosPanelMapping>} */
export const CASH_FLOW_RATIOS_PANEL_MAP = {
    fcfAbsolute: {
        kind: 'notScored',
        reason: 'Absolute currency value; scales with company size. No sector threshold.',
    },
    fcfMargin: {
        kind: 'scored',
        metricId: 'fcfMargin',
    },
    cfiCffLessThanCfo: {
        kind: 'selfSufficiency',
    },
    cfoToNetProfit: {
        kind: 'scored',
        metricId: 'cfoToNetProfit',
    },
    cfoToEbit: {
        kind: 'scored',
        metricId: 'cfoToEbit',
    },
    cfoToEbitda: {
        kind: 'scored',
        metricId: 'cfoToEbitda',
    },
    debtRepaymentToCfo: {
        kind: 'scored',
        metricId: 'debtRepaymentToCfo',
    },
    dividendsToCfo: {
        kind: 'scored',
        metricId: 'dividendsToCfo',
    },
    fcfToNetProfit: {
        kind: 'scored',
        metricId: 'fcfToNetProfit',
    },
    capexToCfoPercent: {
        kind: 'scored',
        metricId: 'capexToCfo',
    },
    capexToSalesPercent: {
        kind: 'scored',
        metricId: 'capexToSales',
    },
    cfoToDebt: {
        kind: 'scored',
        metricId: 'cfoToDebt',
    },
    cashRatio: {
        kind: 'notScored',
        reason: 'Balance-sheet liquidity ratio, not a cash-flow-statement metric.',
    },
    cashFlowCoverageRatio: {
        kind: 'scored',
        metricId: 'cashFlowCoverage',
    },
    ownerEarnings: {
        kind: 'notScored',
        reason: 'Absolute Buffett-style owner earnings (NI + D&A − |CapEx| − ΔWC); no currency threshold.',
    },
    workingCapitalDelta: {
        kind: 'notScored',
        reason: 'Absolute currency working-capital change; scales with company size.',
    },
    cashRoic: {
        kind: 'notScored',
        reason: 'Return metric using an Other-Assets invested-capital proxy; not a clean cash-flow-statement ratio for this system.',
    },
};

/**
 * @param {string} [ratioName] API ratio `name`
 * @returns {CashFlowRatiosPanelMapping}
 */
export function getCashFlowRatiosPanelMapping(ratioName) {
    if (!ratioName) return CASH_FLOW_RATIOS_PANEL_UNMAPPED;
    return CASH_FLOW_RATIOS_PANEL_MAP[ratioName] || CASH_FLOW_RATIOS_PANEL_UNMAPPED;
}

export const CASH_FLOW_RATIOS_PANEL_SCORED_STATUS = new Set(['strong', 'watch', 'weak']);
